import {
  authenticateAccount,
  checkMandate,
  getInfosAccount,
  getToken,
  getTokenIntranet,
  signOut,
  signOutIntranet,
  stopSubscription,
  stopGiftSubscription,
  stopGoCardLessSubscription,
} from '@webapps/business';
import PropTypes from 'prop-types';
/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useEffect, useState, useMemo } from 'react';

import { useRouter } from '../..';
import useLocalStorage from '../hooks/useLocalStorage';
import { useMutation } from './QueryProvider';
import { ROUTES_PATH } from '../../../../../apps/linkpick/src/config/constants';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken, removeAccessToken] =
    useLocalStorage('accessToken');

  const [
    accessTokenIntranet,
    setAccessTokenIntranet,
    removeAccessTokenIntranet,
  ] = useLocalStorage('accessTokenIntranet');
  const [apiToken, setApiToken, removeApiToken] = useLocalStorage('apiToken');

  ///// SUBSCRIPTION TYPE /////
  // Set Premium state to false, on start
  // Will be used to define various access to resources
  const [isPremium, setIsPremium] = useState(null);

  const checkSubscription = async (userInfo) => {

    const subscriptionType = userInfo?.paymentType;
    const subscriptionStatus = userInfo?.subscription;

    if ((subscriptionType?.toLowerCase() === 'stripe' || subscriptionType?.toLowerCase() === 'gift') && subscriptionStatus?.toLowerCase() === 'premium') {
      let subscriptionEndDate = userInfo?.sub_expiration.split('/');
      let formattedSubscriptionEndDate = new Date(
        parseInt(subscriptionEndDate[2], 10),
        parseInt(subscriptionEndDate[1], 10) - 1,
        parseInt(subscriptionEndDate[0], 10)
      );
      let currentDate = new Date();
      if (formattedSubscriptionEndDate > currentDate) {
        setIsPremium(true);
      } else if (subscriptionType?.toLowerCase() === 'stripe') {
        stopSubscriptionMutation.mutateAsync({ accountId: userInfo.id, pennylaneSubscriptionId: userInfo.pennylaneSubscriptionId });
      } else {
        stopSubscriptionGiftMutation.mutateAsync({ accountId: userInfo.id });
      }

    } else if (subscriptionType?.toLowerCase() === 'gocardless' && subscriptionStatus?.toLowerCase() === 'premium') {
      const res = await checkMandateMutation.mutateAsync({ mandateId: userInfo.gocardlessMandateId });
      const mandateStatus = res?.data?.mandate?.status;
      if (mandateStatus === 'active' || mandateStatus === 'pending_customer_approval' || mandateStatus === 'pending_submission' || mandateStatus === 'submitted') {
        setIsPremium(true);
      } else if (mandateStatus === 'failed' || mandateStatus === 'canceled' || mandateStatus === 'expired' || mandateStatus === 'consumed' || mandateStatus === 'suspended_by_payer' || mandateStatus === 'blocked') {
        stopGoCardLessSubscriptionMutation.mutateAsync({ subscriptionId: userInfo.gocardlessSubscriptionId });
        stopSubscriptionMutation.mutateAsync({ accountId: userInfo.id, pennylaneSubscriptionId: userInfo.pennylaneSubscriptionId });
      }
    }
    else if (subscriptionStatus === "free" || profile === "INDIVIDUAL") {
      setIsPremium(false);
    }
    else {
      setIsPremium(null)
    }
  }

  const profile = useMemo(() => {
    if (apiToken) {
      return jwtDecode(apiToken).profile;
    }
    return null;
  }, [apiToken]);

  const userAlias = useMemo(() => {
    if (apiToken) {
      return jwtDecode(apiToken).alias;
    }
    return null;
  }, [apiToken]);

  const idUser = useMemo(() => {
    if (apiToken) {
      return jwtDecode(apiToken).id;
    }
    return null;
  }, [apiToken]);

  const email = useMemo(() => {
    if (apiToken) {
      return jwtDecode(apiToken).email;
    }
    return null;
  }, [apiToken]);

  // Set is Premium according to customer subscription type
  // Need to check account data to set isPremium
  const getSubscriptionMutate = useMutation(getInfosAccount, {
    onSuccess: (data) => {
      const userInfo = data?.data;
      checkSubscription(userInfo);

    },
    onError: (e) => {
    }
  });

  useEffect(() => {
    isPremium === null && getSubscriptionMutate.mutateAsync({ id: idUser });
  }, [isPremium]);

  const { push } = useRouter();

  // Store Cognito accessToken, profile value & email in LS
  const loginMutation = useMutation(getToken, {
    onSuccess: (data) => {
      setAccessToken(data.signInUserSession.accessToken.jwtToken);
    },
  });

  const loginMutationIntranet = useMutation(getTokenIntranet, {
    onSuccess: (data) => {
      setAccessTokenIntranet(data.signInUserSession.accessToken.jwtToken);
    },
  });

  // Account Authentication to get and store API TOKEN
  const accountAuthenticationMutation = useMutation(authenticateAccount, {
    onSuccess: (data) => {
      setApiToken(data?.data?.jwt);
    },
  });

  const stopSubscriptionMutation = useMutation(stopSubscription, {
    onSuccess: () => {
      setIsPremium(false);
    },
  });

  const stopSubscriptionGiftMutation = useMutation(stopGiftSubscription, {
    onSuccess: () => {
      setIsPremium(false);
    },
  });

  const stopGoCardLessSubscriptionMutation = useMutation(stopGoCardLessSubscription, {
    onSuccess: () => {
      setIsPremium(false);
    },
  });

  const checkMandateMutation = useMutation(checkMandate, {
    onSuccess: (data) => {
      return data?.data?.mandate?.status;
    },
  });

  // LOGIN steps
  const login = async ({ onError, onSuccess, password, username }) => {

    await loginMutation.mutateAsync(
      { username, password },

      // If login is successful
      {
        onSuccess: (info) => {
          // Send authentication request to API, to get API TOKEN
          accountAuthenticationMutation.mutateAsync({ usernameOrEmail: username, password: info.username }, {

            // When API Token is stored, request & store USER ALIAS
            onSuccess: (e) => {
              if (e.status !== 200) {
                return;
              }
              localStorage.setItem('expired', 'false');
              getSubscriptionMutate.mutateAsync({ id: jwtDecode(e.data.jwt).id });
            },
            onError
          }
          );

        }, onError
      }
    );

  };

  const loginGoogle = async ({ onError, password, username, jwtToken, email }) => {

    accountAuthenticationMutation.mutateAsync({ usernameOrEmail: username, password }, {

      onSuccess: (e) => {
        if (e.status !== 200) {
          return;
        }
        setApiToken(e.data.jwt);
        setAccessToken(jwtToken);
        localStorage.setItem('expired', 'false');
        getSubscriptionMutate.mutateAsync({ password });
        push(ROUTES_PATH.STUDENT_DASHBOARD);
      },
      onError
    });
  };

  const loginIntranet = async ({ onError, onSuccess, password, username }) => {
    await loginMutationIntranet.mutateAsync(
      { username, password },
      {
        onSuccess: () => {
          accountAuthenticationMutation.mutateAsync({ usernameOrEmail: username, password: username }, {
            onSuccess: (e) => {
              if (e.status !== 200) {
                return;
              }
              localStorage.setItem('expired', 'false');
            },
            onError
          }
          );
        }
      }
    );

  };

  const hasAccess = (accesses = []) =>
    accesses.some((access) => profile === access);

  const logout = (cb = () => { }) => {
    window.location.reload();
    signOut();
    localStorage.removeItem('documentState');
    removeAccessToken();
    removeApiToken();
    cb();
  };

  const logoutIntranet = (cb = () => { }) => {
    window.location.reload();
    signOutIntranet();
    removeAccessTokenIntranet();
    cb();
  };


  // REDIRECTION TO DASHBOARD FOR FREE ACCOUNTS
  const checkPermissions = () => {
    if (!isPremium) {
      profile === 'SCHOOL' ?
        push(ROUTES_PATH.SCHOOL_DASHBOARD)
        : push(ROUTES_PATH.BUSINESS_DASHBOARD);
    }
  }


  const isConnected = loginMutation.isSuccess && !!apiToken;
  const { isLoading } = loginMutation;
  const { isLoading: isLoadingIntranet } = loginMutationIntranet;
  const { error } = loginMutation;
  const { error: errorIntranet } = loginMutationIntranet;
  const { data } = loginMutation;

  return (
    <AuthContext.Provider
      value={{
        login,
        loginGoogle,
        loginIntranet,
        logout,
        logoutIntranet,
        accessToken,
        accessTokenIntranet,
        apiToken,
        profile,
        userAlias,
        email,
        idUser,
        isConnected,
        isLoading,
        isLoadingIntranet,
        isPremium, // Subscription type
        setIsPremium, // Change subscription type
        error,
        errorIntranet,
        data,
        hasAccess,
        checkPermissions, // Redirections for free accounts (Front end restrictions)
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export const useAuth = () => {
  const {
    accessToken,
    accessTokenIntranet,
    apiToken,
    data,
    email,
    error,
    hasAccess,
    idUser,
    isConnected,
    isLoading,
    isPremium, // Subscription type
    setIsPremium, // Change subscription type
    login,
    loginGoogle,
    loginIntranet,
    logout,
    logoutIntranet,
    profile,
    userAlias,
    checkPermissions, // Redirections for free accounts (Front end restrictions)
  } = useContext(AuthContext);
  return {
    data,
    login,
    loginGoogle,
    loginIntranet,
    logout,
    logoutIntranet,
    accessToken,
    accessTokenIntranet,
    apiToken,
    profile,
    email,
    idUser,
    isConnected,
    isLoading,
    isPremium, // Subscription type
    setIsPremium, // Change subscription type
    error,
    hasAccess,
    userAlias,
    checkPermissions, // Redirections for free accounts (Front end restrictions)
  };
};

export default AuthProvider;
